import { useRef, memo, useCallback, useState, useEffect } from 'react';
import { cn, Input } from '@divlab/divanui';
import { IconSearch } from '@divlab/divanui/icons';
import { useDebouncedCallback } from 'use-debounce';

import useTranslation from '@Queries/useTranslation';
import styles from './InputSearch.module.css';

import type { InputProps } from '@divlab/divanui';
import type { FC, MouseEvent, ChangeEvent, FormEvent } from 'react';

export interface InputSearchData extends InputProps {
  className?: string;
  autofocus?: boolean;
  onSearch?: (e: MouseEvent | FormEvent) => void;
  onFocus?: (e: ChangeEvent<HTMLInputElement>) => void;
  searchParamsValue?: string;
  onChangeParams?: (params: string) => void;
}

const InputSearch: FC<InputSearchData> = (props) => {
  const {
    autofocus = false,
    onFocus,
    onBlur,
    searchParamsValue,
    onChangeParams,
    className,
    ...restProps
  } = props;

  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>();
  const { t } = useTranslation();

  const handleFocus = useCallback(
    (e) => {
      if (onFocus) onFocus(e);
      setFocused(true);
    },
    [onFocus],
  );

  const handleBlur = useCallback(
    (e) => {
      if (onBlur) {
        onBlur(e);
      }
      setFocused(false);
    },
    [onBlur],
  );

  const debouceChangeValue = useDebouncedCallback(() => {
    onChangeParams(inputRef.current.value);
  }, 500);

  const handleChange = useCallback(() => {
    debouceChangeValue();
  }, [debouceChangeValue]);

  const handleReset = useCallback(() => {
    debouceChangeValue();
  }, [debouceChangeValue]);

  useEffect(() => {
    if (!autofocus || !inputRef.current) return;
    inputRef.current.focus();
  }, [inputRef, autofocus]);

  return (
    <Input
      {...restProps}
      className={cn(
        styles.input,
        className,
        { [styles.active]: focused },
        { [styles.overrider]: true },
      )}
      type='input'
      name='search'
      placeholder={t('ui.address-or-name')}
      autoComplete='off'
      ref={inputRef}
      value={`${searchParamsValue}`}
      before={
        <button type='submit' className={styles.inputSearchIcon}>
          <IconSearch className={styles.icon} />
        </button>
      }
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onReset={handleReset}
    />
  );
};

export default memo(InputSearch);
